import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  UserGroupIcon,
  AcademicCapIcon,
  ClipboardDocumentCheckIcon,
  BriefcaseIcon,
  PencilIcon,
  PlusIcon,
  CurrencyDollarIcon,
  PercentBadgeIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';

const TrainerSettings = ({ settings = {}, onSettingsChange }) => {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [commissionRate, setCommissionRate] = useState(
    settings?.teacherCommissionRate ? (settings.teacherCommissionRate * 100) : 40
  );

  // Update local state when settings prop changes
  useEffect(() => {
    if (settings?.teacherCommissionRate !== undefined) {
      setCommissionRate(settings.teacherCommissionRate * 100);
    }
  }, [settings?.teacherCommissionRate]);

  // Handle commission rate change
  const handleCommissionRateChange = (value) => {
    const newRate = parseFloat(value);
    if (isNaN(newRate) || newRate < 0 || newRate > 100) {
      toast.error('Le pourcentage doit être entre 0 et 100');
      return;
    }
    
    setCommissionRate(newRate);
    
    // Update settings immediately
    const updatedSettings = {
      ...settings,
      teacherCommissionRate: newRate / 100 // Convert to decimal (40% -> 0.4)
    };
    
    if (onSettingsChange) {
      onSettingsChange(updatedSettings);
      
      // Manually invalidate queries to force refresh across all components
      queryClient.invalidateQueries('organizationSettings');
      queryClient.invalidateQueries('settings');
      
      toast.success(`Commission des formateurs mise à jour: ${newRate}%`);
      console.log('🔍 SETTINGS DEBUG - Commission rate updated to:', newRate + '%', 'Queries invalidated');
    }
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      toast.success('Paramètres des formateurs sauvegardés');
    }, 1000);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50">Paramètres des formateurs</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Catégories de formateurs */}
        <div className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-xl rounded-xl p-6 border border-slate-200 dark:border-slate-600">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4 flex items-center">
            <UserGroupIcon className="h-5 w-5 mr-2 text-blue-400" />
            Catégories de formateurs
          </h3>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
              <thead>
                <tr>
                  <th className="py-3 text-left text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">Nom</th>
                  <th className="py-3 text-left text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">Description</th>
                  <th className="py-3 text-left text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">Taux horaire</th>
                  <th className="py-3 text-center text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                <tr>
                  <td className="py-3 text-sm text-slate-900 dark:text-slate-50">Expert Senior</td>
                  <td className="py-3 text-sm text-slate-900 dark:text-slate-50">Formateur avec plus de 10 ans d'expérience</td>
                  <td className="py-3 text-sm text-slate-900 dark:text-slate-50">3 000 DA</td>
                  <td className="py-3 text-sm text-slate-900 dark:text-slate-50 text-center">
                    <div className="flex justify-center space-x-2">
                      <button type="button" className="p-1 bg-slate-100/80 dark:bg-slate-800/40 rounded text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 hover:bg-slate-100 dark:hover:bg-slate-800/80">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                      <button type="button" className="p-1 bg-slate-100/80 dark:bg-slate-800/40 rounded text-slate-600 dark:text-slate-400 hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-800/80">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="py-3 text-sm text-slate-900 dark:text-slate-50">Formateur Principal</td>
                  <td className="py-3 text-sm text-slate-900 dark:text-slate-50">Formateur avec 5-10 ans d'expérience</td>
                  <td className="py-3 text-sm text-slate-900 dark:text-slate-50">2 500 DA</td>
                  <td className="py-3 text-sm text-slate-900 dark:text-slate-50 text-center">
                    <div className="flex justify-center space-x-2">
                      <button type="button" className="p-1 bg-slate-100/80 dark:bg-slate-800/40 rounded text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 hover:bg-slate-100 dark:hover:bg-slate-800/80">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                      <button type="button" className="p-1 bg-slate-100/80 dark:bg-slate-800/40 rounded text-slate-600 dark:text-slate-400 hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-800/80">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="py-3 text-sm text-slate-900 dark:text-slate-50">Formateur Assistant</td>
                  <td className="py-3 text-sm text-slate-900 dark:text-slate-50">Formateur avec 2-5 ans d'expérience</td>
                  <td className="py-3 text-sm text-slate-900 dark:text-slate-50">1 800 DA</td>
                  <td className="py-3 text-sm text-slate-900 dark:text-slate-50 text-center">
                    <div className="flex justify-center space-x-2">
                      <button type="button" className="p-1 bg-slate-100/80 dark:bg-slate-800/40 rounded text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 hover:bg-slate-100 dark:hover:bg-slate-800/80">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                      <button type="button" className="p-1 bg-slate-100/80 dark:bg-slate-800/40 rounded text-slate-600 dark:text-slate-400 hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-800/80">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="py-3 text-sm text-slate-900 dark:text-slate-50">Formateur Débutant</td>
                  <td className="py-3 text-sm text-slate-900 dark:text-slate-50">Formateur avec moins de 2 ans d'expérience</td>
                  <td className="py-3 text-sm text-slate-900 dark:text-slate-50">1 200 DA</td>
                  <td className="py-3 text-sm text-slate-900 dark:text-slate-50 text-center">
                    <div className="flex justify-center space-x-2">
                      <button type="button" className="p-1 bg-slate-100/80 dark:bg-slate-800/40 rounded text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 hover:bg-slate-100 dark:hover:bg-slate-800/80">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                      <button type="button" className="p-1 bg-slate-100/80 dark:bg-slate-800/40 rounded text-slate-600 dark:text-slate-400 hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-800/80">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4">
            <button
              type="button"
              className="flex items-center space-x-2 px-4 py-2 bg-slate-100/80 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/80 rounded-lg text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 transition-colors"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Ajouter une catégorie</span>
            </button>
          </div>
        </div>

        {/* Exigences de qualification */}
        <div className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-xl rounded-xl p-6 border border-slate-200 dark:border-slate-600">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4 flex items-center">
            <AcademicCapIcon className="h-5 w-5 mr-2 text-purple-400" />
            Exigences de qualification
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Diplômes requis</label>
              <div className="space-y-2">
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="text-purple-500 focus:ring-purple-500 h-4 w-4 rounded"
                    defaultChecked 
                  />
                  <span className="ml-2 text-slate-900 dark:text-slate-50">Licence (Bac+3)</span>
                </label>
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="text-purple-500 focus:ring-purple-500 h-4 w-4 rounded"
                    defaultChecked 
                  />
                  <span className="ml-2 text-slate-900 dark:text-slate-50">Master (Bac+5)</span>
                </label>
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="text-purple-500 focus:ring-purple-500 h-4 w-4 rounded"
                    defaultChecked 
                  />
                  <span className="ml-2 text-slate-900 dark:text-slate-50">Doctorat</span>
                </label>
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="text-purple-500 focus:ring-purple-500 h-4 w-4 rounded"
                    defaultChecked 
                  />
                  <span className="ml-2 text-slate-900 dark:text-slate-50">Certifications professionnelles</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Expérience minimale requise (années)</label>
              <input
                type="number"
                min="0"
                className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                defaultValue="2"
              />
            </div>

            <div>
              <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Certifications valorisées</label>
              <textarea
                rows="3"
                className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                defaultValue="Microsoft Certified Trainer (MCT), Cisco Certified Instructor (CCI), Adobe Certified Instructor (ACI), CompTIA CTT+, PMI-PBA"
              ></textarea>
            </div>
          </div>
        </div>

        {/* Règles d'affectation */}
        <div className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-xl rounded-xl p-6 border border-slate-200 dark:border-slate-600">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4 flex items-center">
            <ClipboardDocumentCheckIcon className="h-5 w-5 mr-2 text-green-400" />
            Règles d'affectation
          </h3>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Nombre maximum d'heures par semaine</label>
                <input
                  type="number"
                  min="1"
                  max="40"
                  className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  defaultValue="24"
                />
              </div>
              <div>
                <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Nombre maximum de cours simultanés</label>
                <input
                  type="number"
                  min="1"
                  className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  defaultValue="3"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Délai minimum entre deux sessions (heures)</label>
                <input
                  type="number"
                  min="0"
                  className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  defaultValue="1"
                />
              </div>
              <div>
                <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Délai de notification (jours)</label>
                <input
                  type="number"
                  min="1"
                  className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  defaultValue="7"
                />
              </div>
            </div>

            <div>
              <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Règles supplémentaires</label>
              <div className="space-y-2">
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="text-purple-500 focus:ring-purple-500 h-4 w-4 rounded"
                    defaultChecked 
                  />
                  <span className="ml-2 text-slate-900 dark:text-slate-50">Affecter les formateurs selon leur spécialité principale</span>
                </label>
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="text-purple-500 focus:ring-purple-500 h-4 w-4 rounded"
                    defaultChecked 
                  />
                  <span className="ml-2 text-slate-900 dark:text-slate-50">Vérifier les disponibilités avant l'affectation</span>
                </label>
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="text-purple-500 focus:ring-purple-500 h-4 w-4 rounded"
                    defaultChecked 
                  />
                  <span className="ml-2 text-slate-900 dark:text-slate-50">Demander confirmation du formateur</span>
                </label>
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="text-purple-500 focus:ring-purple-500 h-4 w-4 rounded" 
                  />
                  <span className="ml-2 text-slate-900 dark:text-slate-50">Permettre les remplacements d'urgence</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Rémunération */}
        <div className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-xl rounded-xl p-6 border border-slate-200 dark:border-slate-600">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4 flex items-center">
            <CurrencyDollarIcon className="h-5 w-5 mr-2 text-yellow-400" />
            Rémunération
          </h3>

          <div className="space-y-4">
            {/* Commission Percentage */}
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
              <h4 className="text-md font-medium text-slate-900 dark:text-slate-50 mb-3 flex items-center">
                <PercentBadgeIcon className="h-5 w-5 mr-2 text-purple-400" />
                Commission des formateurs
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">
                    Pourcentage de commission (%)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={commissionRate}
                      onChange={(e) => handleCommissionRateChange(e.target.value)}
                      className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 pr-8 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="40"
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-600 dark:text-slate-400 text-sm">%</span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Pourcentage des paiements des cours reversé aux formateurs
                  </p>
                </div>
                <div>
                  <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">
                    Aperçu de calcul
                  </label>
                  <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-3 space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400">Paiement étudiant:</span>
                      <span className="text-slate-900 dark:text-slate-50">55,000 DA</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400">Commission formateur ({commissionRate}%):</span>
                      <span className="text-green-400 font-medium">
                        {Math.round(55000 * (commissionRate / 100)).toLocaleString('fr-FR')} DA
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400">École ({(100 - commissionRate).toFixed(1)}%):</span>
                      <span className="text-blue-400 font-medium">
                        {Math.round(55000 * ((100 - commissionRate) / 100)).toLocaleString('fr-FR')} DA
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Mode de calcul principal</label>
                <select className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500">
                  <option value="hourly">Taux horaire</option>
                  <option value="session">Par session</option>
                  <option value="course">Par cours complet</option>
                  <option value="fixed">Montant fixe mensuel</option>
                </select>
              </div>
              <div>
                <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Cycle de paiement</label>
                <select className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500">
                  <option value="monthly">Mensuel</option>
                  <option value="biweekly">Bimensuel</option>
                  <option value="weekly">Hebdomadaire</option>
                  <option value="course_end">Fin de formation</option>
                </select>
              </div>
              <div>
                <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Jour de paiement du mois</label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  defaultValue="30"
                />
              </div>
            </div>

            <div>
              <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Bonus et primes</label>
              <div className="space-y-2">
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="text-purple-500 focus:ring-purple-500 h-4 w-4 rounded"
                    defaultChecked 
                  />
                  <span className="ml-2 text-slate-900 dark:text-slate-50">Prime d'assiduité</span>
                </label>
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="text-purple-500 focus:ring-purple-500 h-4 w-4 rounded"
                    defaultChecked 
                  />
                  <span className="ml-2 text-slate-900 dark:text-slate-50">Prime de satisfaction des étudiants</span>
                </label>
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="text-purple-500 focus:ring-purple-500 h-4 w-4 rounded" 
                  />
                  <span className="ml-2 text-slate-900 dark:text-slate-50">Prime de développement de cours</span>
                </label>
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="text-purple-500 focus:ring-purple-500 h-4 w-4 rounded" 
                  />
                  <span className="ml-2 text-slate-900 dark:text-slate-50">Prime d'ancienneté</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-slate-600 dark:text-slate-400 text-sm font-medium mb-2">Frais remboursables</label>
              <div className="space-y-2">
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="text-purple-500 focus:ring-purple-500 h-4 w-4 rounded"
                    defaultChecked 
                  />
                  <span className="ml-2 text-slate-900 dark:text-slate-50">Transport</span>
                </label>
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="text-purple-500 focus:ring-purple-500 h-4 w-4 rounded" 
                  />
                  <span className="ml-2 text-slate-900 dark:text-slate-50">Repas</span>
                </label>
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="text-purple-500 focus:ring-purple-500 h-4 w-4 rounded" 
                  />
                  <span className="ml-2 text-slate-900 dark:text-slate-50">Matériel pédagogique</span>
                </label>
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="text-purple-500 focus:ring-purple-500 h-4 w-4 rounded" 
                  />
                  <span className="ml-2 text-slate-900 dark:text-slate-50">Formation continue</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            className="px-4 py-2 bg-slate-100/80 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors rounded-lg text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg text-white font-medium shadow hover:from-purple-600 hover:to-blue-600 transition-all duration-200 disabled:opacity-50 flex items-center"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-slate-900 dark:text-slate-50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Enregistrement...
              </>
            ) : 'Enregistrer les modifications'}
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default TrainerSettings;